/**
 * @file FooterLayoutGroup.tsx
 * @description Группа «Расположение» вкладки «Подвал» (phase19.16; SB113,
 * SB114; uiux-footer-columns §4.4, §5; SB122 §4.1): шаблон «В строку /
 * Колонками» карточками и подгруппа «Колонки на ПК · N из 3» со схемой
 * колонок по фактическому меню. При «в строку» схема не прячется молча —
 * на её месте пояснение, сколько пунктов из скольких колонок идут строкой.
 * Ячейка «О магазине» схемы открывает группу «Колонка «О магазине»»
 * (`openGroup`).
 */

import type { SiteTheme } from "@chaiberi/shared";

import { SettingsGroup, SettingsSubgroup, useOpenGroup } from "../../ui/settings-groups";
import { OptionCards } from "../OptionCards";
import { FooterColumnsScheme } from "./FooterColumnsScheme";
import { FooterScheme } from "./LayoutSchemes";
import type { FooterColumnsState } from "../../../hooks/site-view/useFooterColumns";
import { plural } from "../../../lib/site-view/footer-columns";

type Footer = SiteTheme["footer"];

/** Подпись при шаблоне «в строку»: сколько пунктов из скольких колонок идут строкой. */
function rowCaption(items: number, columns: number): string {
  if (columns === 0) return "В подвале «в строку» заголовки колонок меню не видны — пункты идут одной строкой.";
  return `В подвале «в строку» колонок нет: ${items} ${plural(items, "пункт", "пункта", "пунктов")} из ${columns} ${plural(columns, "колонки", "колонок", "колонок")} идут одной строкой, заголовки не видны.`;
}

interface Props {
  footer: Footer;
  columns: FooterColumnsState;
  menuHref: string;
  onChange: (patch: Partial<Footer>) => void;
}

export function FooterLayoutGroup({ footer, columns, menuHref, onChange }: Props) {
  const openGroup = useOpenGroup();
  const layout = columns.layout;
  const shownColumns = layout?.columns.filter((c) => !c.hidden && !c.empty) ?? [];
  const shownItems = shownColumns.reduce((sum, c) => sum + c.visibleCount, 0);
  const menuColumns = layout?.menuShown ? layout.visibleColumns : 0;

  const summary =
    footer.template === "row"
      ? "В строку"
      : !layout && columns.error
        ? "Колонками · не загрузилось"
        : layout
          ? `Колонками · ${menuColumns} ${plural(menuColumns, "колонка", "колонки", "колонок")}`
          : "Колонками";

  return (
    <SettingsGroup
      id="layout"
      title="Расположение"
      tocLabel="Расположение"
      hint="Подвал строкой или колонками."
      summary={summary}
      status={footer.template === "columns" && layout?.overLimit ? "error" : undefined}
    >
      <OptionCards
        aria-label="Шаблон подвала"
        columns={2}
        value={footer.template}
        onChange={(template) => onChange({ template })}
        options={[
          { value: "row", label: "В строку", preview: <FooterScheme template="row" /> },
          { value: "columns", label: "Колонками", preview: <FooterScheme template="columns" /> },
        ]}
      />
      {footer.template === "row" ? (
        <p className="type-caption text-on-surface-variant">{rowCaption(shownItems, layout?.menuShown ? shownColumns.length : 0)}</p>
      ) : (
        <SettingsSubgroup title={layout ? `Колонки на ПК · ${layout.visibleColumns} из ${layout.limit}` : "Колонки на ПК"}>
          <FooterColumnsScheme
            layout={layout}
            error={columns.error}
            onRetry={columns.reload}
            categoriesKnown={columns.categoriesKnown}
            menuHref={menuHref}
            copyrightInAbout={footer.show.copyright && footer.legal.copyrightPlace === "about"}
            onAbout={() => openGroup("about")}
          />
        </SettingsSubgroup>
      )}
    </SettingsGroup>
  );
}
